/**
 * pdp-tlpc.js
 * Keeps the PDP product gallery (desktop Figma mosaic + mobile Swiper,
 * both rendered by snippets/product-media-gallery.liquid) filtered to the
 * selected color variant using each image's ALT text.
 *
 * - Initial render is already filtered server-side in Liquid (no flash).
 * - This file only reacts to later color changes.
 * - Desktop is a static grid (every image visible at once) — filtering is
 *   just a class toggle plus reassigning which tile is the "hero"; nothing
 *   to re-scroll or re-page.
 * - Mobile rebuilds the theme's existing Swiper (already loaded in
 *   layout/theme.liquid — no second library) with only the currently
 *   visible slides re-attached, so the segmented pagination bar always has
 *   exactly one segment per visible image and never sits on a hidden slide.
 * - Listens on the theme's own PUB_SUB_EVENTS.variantChange, published by
 *   assets/product-info.js on every variant swap.
 *
 * Also handles two things for the Size Guide modal
 * (sections/main-product.liquid):
 * - The filled dot. Chart rows are rendered server-side from the product's
 *   custom.size_map metaobject, and each Size cell that matches a real Size
 *   option value is a native <label for> pointing at that value's real
 *   <input> in the main variant picker — clicking a row is already the real
 *   selection, no JS needed. This file only toggles the dot to match
 *   whichever size is actually checked, on load and on every variant change.
 * - The CM column. Only inch values are stored in the metaobject, so CM is
 *   derived here on load and swapped in when the IN/CM toggle changes.
 */
(function () {
  'use strict';

  function normalize(value) {
    return (value || '').toString().trim().toLowerCase();
  }

  function getColorFromVariant(variant, optionIndex) {
    if (!variant || !optionIndex || !Array.isArray(variant.options)) return null;
    return variant.options[optionIndex - 1];
  }

  // Returns { normalizedColor, hasMatch } and leaves every [data-media-color]
  // element under root with the correct .pdp-tlpc-media--hidden state.
  function filterSlides(root, allSlides, color) {
    const normalizedColor = normalize(color);
    const matches = normalizedColor ? allSlides.filter((el) => el.dataset.mediaColor === normalizedColor) : [];
    // Fallback: if nothing matches (e.g. ALT tagging isn't finished yet),
    // leave every slide visible instead of showing a blank gallery.
    const hasMatch = matches.length > 0;

    allSlides.forEach((el) => {
      // Untagged media (blank ALT — some product photos never got a color
      // tag) is never hidden, same as the Liquid-side initial render: it's
      // treated as a shared/fallback image shown for every color rather
      // than an orphaned photo that vanishes because it matches nothing.
      const isUntagged = !el.dataset.mediaColor;
      el.classList.toggle(
        'pdp-tlpc-media--hidden',
        hasMatch && !isUntagged && el.dataset.mediaColor !== normalizedColor
      );
    });

    return { normalizedColor, hasMatch };
  }

  // Makes `mediaItem` (an .product__media-item li) the hero: flags it
  // .is-active (drives the square crop via pdp-tlpc.css) and physically
  // moves it to the front of the grid, so it's always the first/big tile
  // regardless of where it started. Toggling the class alone isn't enough
  // — Dawn's grid renders in DOM order, so a class-only "active" flag on a
  // tile sitting in position 2 stays visually in position 2.
  function makeHero(mediaItem) {
    const list = mediaItem.closest('.product__media-list');
    if (!list) return;
    Array.from(list.children).forEach((el) => el.classList.remove('is-active'));
    mediaItem.classList.add('is-active');
    if (list.firstElementChild !== mediaItem) list.prepend(mediaItem);
  }

  // Keeps the "only one image left" state in sync after a colour switch.
  // Liquid sets the same class on first render (no flash); here it can flip
  // either way, since a colour with one tagged photo and a colour with five
  // are the same gallery. Thumbnails carry [data-media-color] too, so count
  // real tiles only, or a rail entry would make a single image look plural.
  function updateSingleState(root, slides) {
    const tiles = slides.filter(
      (el) =>
        el.classList.contains('product__media-item') || el.classList.contains('pdp-tlpc-gallery-mobile__slide')
    );
    const visible = tiles.filter((el) => !el.classList.contains('pdp-tlpc-media--hidden'));
    root.classList.toggle('pdp-tlpc-gallery--single', visible.length < 2);
  }

  function updateDesktopHero(root, allSlides, normalizedColor, hasMatch) {
    let hero = hasMatch ? allSlides.find((el) => el.dataset.mediaColor === normalizedColor) : null;
    if (!hero) {
      hero = allSlides.find((el) => !el.classList.contains('pdp-tlpc-media--hidden')) || allSlides[0];
    }
    if (hero) makeHero(hero);
  }

  // Dawn's own thumbnail click handler (media-gallery.js) only toggles
  // .is-active and scrolls the (now-hidden) carousel track — it never
  // reorders the DOM, since normal Dawn galleries show one slide at a time
  // and don't need to. Our desktop gallery is a static mosaic instead, so
  // clicking a thumbnail has to promote that image to the front itself.
  function initHeroReorderOnClick(root) {
    root.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-target]');
      if (!thumb) return;
      const list = root.querySelector('.product__media-list');
      const mediaItem = list && list.querySelector(`[data-media-id="${thumb.dataset.target}"]`);
      if (mediaItem) makeHero(mediaItem);
    });
  }

  // ---- Size Guide modal: the chart table's own Size column is the real
  // selector. Each row's <label for> is rendered server-side (Liquid loops
  // product.options_with_values directly — see sections/main-product.liquid),
  // already pointing at that value's real <input> in the main variant
  // picker, so clicking it is already the real selection with zero JS.
  // The only thing left to do here is keep the filled dot in sync. ----

  // Keeps the filled dot pointed at whichever size is actually selected on
  // the real product form (reads the real input's checked state directly).
  // Cells whose text has no matching Size option render as a <span> with no
  // [for] — they simply never light up.
  function syncSizeGuideTableRows() {
    document.querySelectorAll('.pdp-tlpc-sizeguide-modal__size-cell').forEach((cell) => {
      const target = cell.getAttribute('for');
      const input = target ? document.getElementById(target) : null;
      cell.classList.toggle('is-selected', !!(input && input.checked));
    });
  }

  // ---- Size Guide modal: IN / CM.
  // Only inches are stored in the size_map metaobject, so the CM column is
  // derived here: every number in a cell is multiplied by 2.54 and rounded,
  // which keeps ranges ("32-33" -> "81-84") and any surrounding text intact.
  // The Size column (first cell of each row) is never converted.
  const INCH_TO_CM = 2.54;

  function inchesToCm(value) {
    return value.replace(/\d+(?:\.\d+)?/g, (match) => String(Math.round(parseFloat(match) * INCH_TO_CM)));
  }

  function setSizeGuideUnit(modal, unit) {
    modal.querySelectorAll('[data-size-chart] td[data-sg-in]').forEach((cell) => {
      cell.textContent = unit === 'cm' ? cell.dataset.sgCm : cell.dataset.sgIn;
    });
  }

  // ---- Size Guide modal: keep the CTA price current.
  // Dawn only re-renders its own #price-<sectionId> block on a variant swap
  // (see updateSourceFromDestination in product-info.js), so the popup's copy
  // of the price is mirrored from it rather than left at its server-rendered
  // value. Reads the visible price item only — sale price when on sale,
  // regular otherwise — so the compare-at never leaks into the button.
  function readCurrentPrice(source) {
    const price = source.querySelector('.price');
    if (!price) return '';
    const item = price.classList.contains('price--on-sale')
      ? price.querySelector('.price-item--sale')
      : price.querySelector('.price__regular .price-item--regular');
    return item ? item.textContent.trim() : '';
  }

  function initSizeGuidePrice() {
    const targets = document.querySelectorAll('[data-sg-price]');
    if (!targets.length) return;

    const source = Array.from(document.querySelectorAll('[id^="price-"]')).find(
      (el) => !el.closest('.pdp-tlpc-sizeguide-modal')
    );
    if (!source) return;

    const sync = () => {
      const value = readCurrentPrice(source);
      if (!value) return;
      targets.forEach((target) => {
        if (target.textContent.trim() !== value) target.textContent = value;
      });
    };

    sync();
    new MutationObserver(sync).observe(source, { childList: true, subtree: true, characterData: true });
  }

  function initSizeGuideUnits() {
    document.querySelectorAll('.pdp-tlpc-sizeguide-modal').forEach((modal) => {
      const cells = modal.querySelectorAll('[data-size-chart] td[data-sg-in]');
      if (!cells.length) return;

      cells.forEach((cell) => {
        cell.dataset.sgCm = inchesToCm(cell.dataset.sgIn || '');
      });

      const inputs = modal.querySelectorAll('.pdp-tlpc-sizeguide-modal__unit-input');
      if (!inputs.length) return;

      inputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) setSizeGuideUnit(modal, input.value);
        });
      });

      const checked = modal.querySelector('.pdp-tlpc-sizeguide-modal__unit-input:checked');
      setSizeGuideUnit(modal, checked ? checked.value : 'in');
    });
  }

  function rebuildMobileSwiper(root) {
    if (typeof Swiper === 'undefined') return;
    const wrapper = root.querySelector('.swiper-wrapper');
    if (!wrapper || !root._pdpAllSlides) return;

    if (root.swiper) {
      root.swiper.destroy(true, false);
      root.swiper = null;
    }

    const visible = root._pdpAllSlides.filter((el) => !el.classList.contains('pdp-tlpc-media--hidden'));
    const toShow = visible.length ? visible : root._pdpAllSlides;
    wrapper.innerHTML = '';
    toShow.forEach((el) => wrapper.appendChild(el));

    const paginationEl = root.querySelector('[data-mobile-gallery-pagination]');
    root.swiper = new Swiper(root, {
      slidesPerView: 1.25,
      spaceBetween: 0,
      watchOverflow: true,
      pagination: paginationEl ? { el: paginationEl, clickable: true } : false,
    });
  }

  function init() {
    const desktopGalleries = Array.from(document.querySelectorAll('[data-color-gallery]')).map((root) => {
      initHeroReorderOnClick(root);
      return {
        root,
        isMobile: false,
        slides: Array.from(root.querySelectorAll('[data-media-color]')),
      };
    });

    const mobileGalleries = Array.from(document.querySelectorAll('[data-color-gallery-mobile]')).map((root) => {
      const slides = Array.from(root.querySelectorAll('[data-media-color]'));
      root._pdpAllSlides = slides;
      rebuildMobileSwiper(root);
      return { root, isMobile: true, slides };
    });

    syncSizeGuideTableRows();
    initSizeGuideUnits();
    initSizeGuidePrice();

    const galleries = desktopGalleries.concat(mobileGalleries);
    const hasSizeGuideTable = document.querySelector('.pdp-tlpc-sizeguide-modal__size-cell') !== null;
    if (!galleries.length && !hasSizeGuideTable) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event && event.data && event.data.variant;
      if (!variant) return;

      galleries.forEach(({ root, isMobile, slides }) => {
        const optionIndex = parseInt(root.dataset.colorOptionIndex, 10);
        const color = getColorFromVariant(variant, optionIndex);
        const { normalizedColor, hasMatch } = filterSlides(root, slides, color);
        updateSingleState(root, slides);

        if (isMobile) {
          rebuildMobileSwiper(root);
        } else {
          updateDesktopHero(root, slides, normalizedColor, hasMatch);
        }
      });

      syncSizeGuideTableRows();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();